import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Textarea,
  Switch,
  Select,
  SelectItem,
  addToast,
} from '@heroui/react';
import { Loader2, Edit3, Trash2, Plus } from 'lucide-react';
import { Button } from '@/shared/ui/components/Button';
import { cn } from '@/shared/lib/utils';
import { logger } from '@/shared/lib';
import {
  useActionPresets,
  useUpdateActionPreset,
  useDeleteActionPreset,
  useCreateActionPreset,
} from '@/entities/action-preset';
import type { 
  ActionPresetResponse, 
  ActionPresetUpdateRequest,
  ActionPresetCreateRequest,
} from '@/shared/api/generated';

interface ActionPresetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onActionPresetUpdated?: () => void;
}

interface EditingPreset {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  payload_schema: string; // JSON string for editing
}

interface NewPreset {
  platform: string;
  action: string;
  name: string;
  description: string;
  payload_schema: string; // JSON string for editing
}

export const ActionPresetModal: React.FC<ActionPresetModalProps> = ({
  isOpen,
  onClose,
  onActionPresetUpdated,
}) => {
  const [editingPreset, setEditingPreset] = useState<EditingPreset | null>(null);
  const [newPreset, setNewPreset] = useState<NewPreset | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Fetch action presets
  const { data: actionPresetsData, isLoading: isLoadingPresets } = useActionPresets(
    {},
    isOpen // Only fetch when modal is open
  );

  // Mutations
  const { mutate: createActionPreset, isPending: isCreatingPreset } = useCreateActionPreset();
  const { mutate: updateActionPreset, isPending: isUpdating } = useUpdateActionPreset();
  const { mutate: deleteActionPreset, isPending: isDeleting } = useDeleteActionPreset();

  const actionPresets = actionPresetsData?.data?.action_presets ?? [];

  // Get unique platforms for creation
  const availablePlatforms = ['instagram', 'youtube', 'tiktok', 'facebook', 'twitter'];

  const handleCreatePreset = () => {
    setNewPreset({
      platform: '',
      action: '',
      name: '',
      description: '',
      payload_schema: '{\n  "type": "object",\n  "properties": {},\n  "required": []\n}',
    });
    setIsCreating(true);
  };

  const handleEditPreset = (preset: ActionPresetResponse) => {
    setEditingPreset({
      id: preset.id,
      name: preset.name,
      description: preset.description || '',
      is_active: preset.is_active,
      payload_schema: JSON.stringify(preset.payload_schema, null, 2),
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditingPreset(null);
    setIsEditing(false);
  };

  const handleCancelCreate = () => {
    setNewPreset(null);
    setIsCreating(false);
  };

  const handleSavePreset = () => {
    if (!editingPreset) return;

    // Validate JSON schema
    let parsedSchema;
    try {
      parsedSchema = JSON.parse(editingPreset.payload_schema);
    } catch (error) {
      addToast({
        title: '잘못된 JSON 형식',
        description: 'Payload Schema는 유효한 JSON 형식이어야 합니다.',
        color: 'warning',
      });
      return;
    }

    const updateData: ActionPresetUpdateRequest = {
      name: editingPreset.name,
      description: editingPreset.description,
      payload_schema: parsedSchema,
      is_active: editingPreset.is_active,
    };

    updateActionPreset(
      {
        path: { id: editingPreset.id },
        body: updateData,
      },
      {
        onSuccess: () => {
          logger.info('Action preset updated successfully');
          addToast({
            title: '작업 프리셋 수정 완료',
            description: '작업 프리셋이 성공적으로 수정되었습니다.',
            color: 'success',
          });
          handleCancelEdit();
          onActionPresetUpdated?.();
        },
        onError: (error) => {
          logger.error('Failed to update action preset:', error);
          addToast({
            title: '작업 프리셋 수정 실패',
            description: '작업 프리셋 수정에 실패했습니다. 다시 시도해주세요.',
            color: 'danger',
          });
        },
      }
    );
  };

  const handleCreateNewPreset = () => {
    if (!newPreset) return;

    // Validate required fields
    if (!newPreset.platform || !newPreset.action || !newPreset.name) {
      addToast({
        title: '필수 항목 입력 필요',
        description: '플랫폼, 액션, 이름은 필수 입력 항목입니다.',
        color: 'warning',
      });
      return;
    }

    // Validate JSON schema
    let parsedSchema;
    try {
      parsedSchema = JSON.parse(newPreset.payload_schema);
    } catch (error) {
      addToast({
        title: '잘못된 JSON 형식',
        description: 'Payload Schema는 유효한 JSON 형식이어야 합니다.',
        color: 'warning',
      });
      return;
    }

    const createData: ActionPresetCreateRequest = {
      platform: newPreset.platform,
      action: newPreset.action,
      name: newPreset.name,
      description: newPreset.description,
      payload_schema: parsedSchema,
    };

    createActionPreset(
      { body: createData },
      {
        onSuccess: () => {
          logger.info('Action preset created successfully');
          addToast({
            title: '작업 프리셋 생성 완료',
            description: '새로운 작업 프리셋이 성공적으로 생성되었습니다.',
            color: 'success',
          });
          handleCancelCreate();
          onActionPresetUpdated?.();
        },
        onError: (error) => {
          logger.error('Failed to create action preset:', error);
          addToast({
            title: '작업 프리셋 생성 실패',
            description: '작업 프리셋 생성에 실패했습니다. 다시 시도해주세요.',
            color: 'danger',
          });
        },
      }
    );
  };

  const handleDeletePreset = (id: number, name: string) => {
    if (!confirm(`"${name}" 작업 프리셋을 삭제하시겠습니까?`)) {
      return;
    }

    deleteActionPreset(
      { path: { id } },
      {
        onSuccess: () => {
          logger.info('Action preset deleted successfully');
          addToast({
            title: '작업 프리셋 삭제 완료',
            description: '작업 프리셋이 성공적으로 삭제되었습니다.',
            color: 'success',
          });
          onActionPresetUpdated?.();
        },
        onError: (error) => {
          logger.error('Failed to delete action preset:', error);
          addToast({
            title: '작업 프리셋 삭제 실패',
            description: '작업 프리셋 삭제에 실패했습니다. 다시 시도해주세요.',
            color: 'danger',
          });
        },
      }
    );
  };

  const handleModalClose = () => {
    handleCancelEdit();
    handleCancelCreate();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      size="5xl"
      scrollBehavior="inside"
      classNames={{
        backdrop: "bg-black/50",
        base: "bg-white",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center justify-between pr-4">
            <div>
              <h3 className="text-lg font-semibold">작업 프리셋 관리</h3>
              <p className="text-sm text-gray-500">작업 프리셋을 생성, 수정하거나 삭제할 수 있습니다.</p>
            </div>
            <div className="flex-shrink-0">
              <Button 
                size="sm" 
                onClick={handleCreatePreset}
                disabled={isCreating || isEditing || isCreatingPreset || isUpdating || isDeleting}
              >
                <Plus className="w-4 h-4 mr-1" />
                새 프리셋 생성
              </Button>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="py-6">
          {/* Create new preset form */}
          {isCreating && newPreset && (
            <div className="mb-6 p-4 border border-primary/20 rounded-lg bg-primary/5">
              <h4 className="font-medium text-lg mb-4">새 작업 프리셋 생성</h4>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="플랫폼"
                    placeholder="플랫폼을 선택하세요"
                    selectedKeys={newPreset.platform ? [newPreset.platform] : []}
                    onSelectionChange={(keys) => {
                      const platform = Array.from(keys)[0] as string;
                      setNewPreset(prev => prev ? { ...prev, platform: platform || '' } : null);
                    }}
                    classNames={{
                      trigger: "bg-white border border-gray-300"
                    }}
                    isRequired
                  >
                    {availablePlatforms.map((platform) => (
                      <SelectItem key={platform}>
                        {platform.toUpperCase()}
                      </SelectItem>
                    ))}
                  </Select>

                  <Input
                    label="액션"
                    placeholder="액션명을 입력하세요"
                    value={newPreset.action}
                    onChange={(e) =>
                      setNewPreset(prev =>
                        prev ? { ...prev, action: e.target.value } : null
                      )
                    }
                    classNames={{
                      input: "bg-white",
                      inputWrapper: "bg-white border border-gray-300",
                    }}
                    isRequired
                  />
                </div>

                <Input
                  label="프리셋 이름"
                  placeholder="프리셋 이름을 입력하세요"
                  value={newPreset.name}
                  onChange={(e) =>
                    setNewPreset(prev =>
                      prev ? { ...prev, name: e.target.value } : null
                    )
                  }
                  classNames={{
                    input: "bg-white",
                    inputWrapper: "bg-white border border-gray-300",
                  }}
                  isRequired
                />

                <Textarea
                  label="설명"
                  placeholder="프리셋 설명을 입력하세요 (선택사항)"
                  value={newPreset.description}
                  onChange={(e) =>
                    setNewPreset(prev =>
                      prev ? { ...prev, description: e.target.value } : null
                    )
                  }
                  minRows={2}
                  classNames={{
                    input: "bg-white",
                    inputWrapper: "bg-white border border-gray-300",
                  }}
                />

                <Textarea
                  label="Payload Schema (JSON)"
                  value={newPreset.payload_schema}
                  onChange={(e) =>
                    setNewPreset(prev =>
                      prev ? { ...prev, payload_schema: e.target.value } : null
                    )
                  }
                  minRows={6}
                  classNames={{
                    input: "bg-white font-mono text-sm",
                    inputWrapper: "bg-white border border-gray-300",
                  }}
                  placeholder="유효한 JSON 스키마를 입력하세요..."
                  isRequired
                />

                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelCreate}
                    disabled={isCreatingPreset}
                  >
                    취소
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCreateNewPreset}
                    disabled={isCreatingPreset}
                  >
                    {isCreatingPreset ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : null}
                    생성
                  </Button>
                </div>
              </div>
            </div>
          )}

          {isLoadingPresets ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 mr-2 animate-spin" />
              <span>작업 프리셋을 불러오는 중...</span>
            </div>
          ) : actionPresets.length === 0 && !isCreating ? (
            <div className="text-center py-8 text-gray-500">
              등록된 작업 프리셋이 없습니다.
            </div>
          ) : (
            <div className="space-y-4">
              {actionPresets.map((preset) => (
                <div
                  key={preset.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  {isEditing && editingPreset?.id === preset.id ? (
                    // Edit mode
                    <div className="space-y-4">
                      <Input
                        label="프리셋 이름"
                        value={editingPreset.name}
                        onChange={(e) =>
                          setEditingPreset(prev =>
                            prev ? { ...prev, name: e.target.value } : null
                          )
                        }
                        classNames={{
                          input: "bg-white",
                          inputWrapper: "bg-white border border-gray-300",
                        }}
                      />

                      <Textarea
                        label="설명"
                        value={editingPreset.description}
                        onChange={(e) =>
                          setEditingPreset(prev =>
                            prev ? { ...prev, description: e.target.value } : null
                          )
                        }
                        minRows={2}
                        classNames={{
                          input: "bg-white",
                          inputWrapper: "bg-white border border-gray-300",
                        }}
                      />

                      <Textarea
                        label="Payload Schema (JSON)"
                        value={editingPreset.payload_schema}
                        onChange={(e) =>
                          setEditingPreset(prev =>
                            prev ? { ...prev, payload_schema: e.target.value } : null
                          )
                        }
                        minRows={6}
                        classNames={{
                          input: "bg-white font-mono text-sm",
                          inputWrapper: "bg-white border border-gray-300",
                        }}
                        placeholder="유효한 JSON 스키마를 입력하세요..."
                      />

                      <div className="flex items-center justify-between">
                        <Switch
                          isSelected={editingPreset.is_active}
                          onValueChange={(value) =>
                            setEditingPreset(prev =>
                              prev ? { ...prev, is_active: value } : null
                            )
                          }
                        >
                          활성 상태
                        </Switch>

                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelEdit}
                            disabled={isUpdating}
                          >
                            취소
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSavePreset}
                            disabled={isUpdating}
                          >
                            {isUpdating ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : null}
                            저장
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h4 className="font-medium text-lg">{preset.name}</h4>
                            <span className={cn(
                              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                              preset.is_active
                                ? 'bg-success/10 text-success'
                                : 'bg-gray-100 text-gray-600'
                            )}>
                              {preset.is_active ? '활성' : '비활성'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {preset.description || '설명이 없습니다.'}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span>플랫폼: {preset.platform.toUpperCase()}</span>
                            <span>액션: {preset.action}</span>
                          </div>
                        </div>

                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditPreset(preset)}
                            disabled={isEditing || isDeleting}
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            수정
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeletePreset(preset.id, preset.name)}
                            disabled={isEditing || isDeleting}
                            className="text-danger hover:bg-danger/10 hover:border-danger"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            삭제
                          </Button>
                        </div>
                      </div>

                      {/* Payload Schema preview */}
                      <details className="mt-3">
                        <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                          Payload Schema 보기
                        </summary>
                        <pre className="mt-2 p-3 bg-gray-50 rounded text-xs font-mono overflow-x-auto">
                          {JSON.stringify(preset.payload_schema, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={handleModalClose}
            disabled={isUpdating || isDeleting}
          >
            닫기
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};